// Catálogo de peças compartilhadas do sistema — fonte única da tela
// Configurações → Catálogo de Componentes.
//
// Por que isto existe: conforme o sistema cresce, peças (componentes visuais e
// comportamentos/hooks) viram reutilizáveis pra não duplicar código — muda num
// lugar, muda em todas as telas que usam. Este catálogo é o lugar pra NÃO se
// perder: o que já temos pronto pra reusar, o que cada peça faz e onde aparece.
//
// Convenção (decidida com o Ruan): o NÚMERO vive aqui no catálogo, NUNCA no nome
// do arquivo (senão os "endereços" no código quebram ao renumerar). Ao criar uma
// peça compartilhada nova, acrescente uma linha aqui no fechamento da sessão.
//
// Tipos:
//  - 'componente'   = um pedaço de tela pronto (um "móvel" que aparece).
//  - 'comportamento'= uma regra reutilizada, sem desenhar nada (um hook).

export type TipoPeca = 'componente' | 'comportamento';

export interface PecaCatalogo {
  /** Número de catálogo, zero-padded (001, 002...). Só identifica na lista — não é o nome do arquivo. */
  numero: string;
  /** Nome amigável da peça (pelo que ela faz). */
  nome: string;
  tipo: TipoPeca;
  /** O que ela faz, em linguagem de negócio. */
  oQueFaz: string;
  /** Onde aparece hoje (telas/módulos que usam). */
  ondeUsada: string;
  /** Arquivo no código — pra rastreio quando for mexer. */
  arquivo: string;
}

// Ordenado por número. Mantenha numeração contínua; peça nova entra no fim.
export const CATALOGO_COMPONENTES: PecaCatalogo[] = [
  {
    numero: '001',
    nome: 'Modal padrão',
    tipo: 'componente',
    oQueFaz: 'Janela sobreposta arrastável pelo cabeçalho, que só fecha no X / Cancelar / Esc (nunca por clique no fundo). Herda o tema.',
    ondeUsada: 'Sistema inteiro — todo modal de cadastro, confirmação e operação.',
    arquivo: 'components/Modal.tsx',
  },
  {
    numero: '002',
    nome: 'Menu de ações da linha (⋮)',
    tipo: 'componente',
    oQueFaz: 'O menuzinho de três pontos no fim da linha de uma lista, com as ações (editar, inativar, reativar...). Item perigoso fica vermelho só dentro do menu.',
    ondeUsada: 'Cadastros: Clientes, Fornecedores, Produtos, Usuários.',
    arquivo: 'components/MenuAcoesLinha.tsx',
  },
  {
    numero: '003',
    nome: 'Selo Ativo / Inativo',
    tipo: 'componente',
    oQueFaz: 'O selo de status do cadastro. Usa a cor da marca vs cinza (nunca verde/vermelho), seguro pra daltonismo.',
    ondeUsada: 'Cadastros que ligam/desligam registros.',
    arquivo: 'components/StatusBadge.tsx',
  },
  {
    numero: '004',
    nome: 'Bloco de formulário',
    tipo: 'componente',
    oQueFaz: 'Agrupa os campos de um cadastro em seções com título + ícone + divisória, cor pelo tema.',
    ondeUsada: 'Cadastros: Clientes, Fornecedores, Usuários.',
    arquivo: 'components/SecaoForm.tsx',
  },
  {
    numero: '005',
    nome: 'Seletor com busca (ComboBox)',
    tipo: 'componente',
    oQueFaz: 'Caixa de seleção que filtra ao digitar e navega por ↑/↓ + Enter no teclado.',
    ondeUsada: 'Vários formulários e filtros do sistema.',
    arquivo: 'components/ComboBox.tsx',
  },
  {
    numero: '006',
    nome: 'Campo de dinheiro (R$)',
    tipo: 'componente',
    oQueFaz: 'Campo monetário no padrão pt-BR (formata ao sair, aceita vírgula). Nunca usa campo numérico cru pra reais.',
    ondeUsada: 'Caixa, Contas a Pagar/Receber, e onde se digita valor.',
    arquivo: 'components/inputs/MoneyInput.tsx',
  },
  {
    numero: '007',
    nome: 'Campo em maiúsculas',
    tipo: 'componente',
    oQueFaz: 'Campo de texto que grava em CAIXA ALTA, pros cadastros padronizarem o conteúdo.',
    ondeUsada: 'Cadastros que gravam texto (nomes, razão social...).',
    arquivo: 'components/inputs/UpperInput.tsx',
  },
  {
    numero: '008',
    nome: 'Campo de telefone',
    tipo: 'componente',
    oQueFaz: 'Campo de telefone/WhatsApp com máscara brasileira.',
    ondeUsada: 'Cadastros de Clientes, Fornecedores, contatos.',
    arquivo: 'components/inputs/PhoneInput.tsx',
  },
  {
    numero: '009',
    nome: 'Grid navegável',
    tipo: 'comportamento',
    oQueFaz: 'Faz uma tabela responder ao teclado: clicar marca a linha em amarelo, ↑/↓ navegam, Enter/duplo-clique faz a ação principal, e a linha acompanha o scroll.',
    ondeUsada: 'Produtos, Fornecedores, Caixa (Pedidos pra finalizar).',
    arquivo: 'lib/useGridNavegavel.ts',
  },
  {
    numero: '010',
    nome: 'Cabeçalho fixo (sticky)',
    tipo: 'comportamento',
    oQueFaz: 'Mantém o cabeçalho da tela/lista fixo no topo enquanto a página rola.',
    ondeUsada: 'Listas e telas longas (Caixa, cadastros).',
    arquivo: 'lib/useStickyHeader.ts',
  },
  {
    numero: '011',
    nome: 'Navegação por teclado em listas suspensas',
    tipo: 'comportamento',
    oQueFaz: 'Padroniza ↑/↓ (destaca + rola) e Enter (seleciona) dentro de autocompletes e listas suspensas.',
    ondeUsada: 'ComboBox e autocompletes de busca (produto, cliente, contato).',
    arquivo: 'lib/useListaTeclado.ts',
  },
  {
    numero: '012',
    nome: 'Enter pula pro próximo campo',
    tipo: 'comportamento',
    oQueFaz: 'Faz o Enter avançar o foco pro campo seguinte (Shift+Enter volta), como o Tab — em formulários do sistema todo.',
    ondeUsada: 'Global — todos os formulários.',
    arquivo: 'lib/enter-as-tab.ts',
  },
  {
    numero: '013',
    nome: 'Pix do balcão (cobrança)',
    tipo: 'componente',
    oQueFaz: 'Peça única que faz o ciclo completo de uma cobrança Pix no balcão: cobra ao montar, mostra o QR na tela do cliente, acompanha o pagamento, confirma sozinho (número do Pix ou valor quebrado único) ou deixa o operador escolher quem pagou quando só o valor bate, dispara o comprovante 🖨 e cancela o QR sozinho se a linha sair de cena. Devolve a bola pra quem usa (pago / valor ajustado / cancelado). Funde a antiga dupla "Cartão do QR Pix" (visual) + "Máquina de cobrança Pix" (lógica).',
    ondeUsada: 'Caixa: Finalizar Venda e Receber Avulso. Contas a Receber: Receber Título (individual e lote), alvo "título" — cobra e baixa conciliando sozinho (DA-258).',
    arquivo: 'components/caixa/CobrancaPix.tsx',
  },
  {
    numero: '014',
    nome: 'Simulação de parcelamento por WhatsApp',
    tipo: 'componente',
    oQueFaz: 'Botão que gera a imagem do extrato de parcelamento no cartão (as parcelas já cotadas — só formata, nunca recalcula) e abre o envio por WhatsApp com ela pronta, reusando o cano de envio do orçamento. Irmã das peças 013/016 (peças do balcão).',
    ondeUsada: 'Orçamento: Enviar ao Caixa. Caixa: Finalizar Venda.',
    arquivo: 'components/pagamento/SimulacaoParcelamentoWhatsApp.tsx',
  },
  {
    numero: '015',
    nome: 'Identificador de Componentes (Modo Identificar)',
    tipo: 'comportamento',
    oQueFaz: 'Lente administrativa que, quando ligada, contorna cada componente compartilhado visível na tela e exibe seu número e nome do Catálogo. Serve para o administrador identificar quais componentes estão em uso em qualquer tela sem abrir o código. Invisível para usuários comuns — só aparece quando um admin ativa o modo.',
    ondeUsada: 'Global — todas as telas (admin)',
    arquivo: 'components/IdentificadorComponentes.tsx',
  },
  {
    numero: '016',
    nome: 'Cartão do balcão (cobrança)',
    tipo: 'componente',
    oQueFaz: 'Peça única que faz o ciclo completo de uma cobrança no cartão pela maquininha Mercado Pago Point — crédito E débito numa peça só. Cobra ao montar: manda o valor pro terminal, acompanha (poll + aviso do servidor), aprova sozinho quando o cliente passa o cartão, ou deixa tentar de novo se recusar. Devolve a bola pra quem usa (aprovado / cancelado). Irmã da peça 013 (Pix do balcão): mesma forma, sem QR — o cliente mexe na maquininha.',
    ondeUsada: 'Caixa: Finalizar Venda e Receber Avulso. Contas a Receber: Receber Título (individual e lote), alvo "título" — cobra e baixa conciliando sozinho (DA-258).',
    arquivo: 'components/caixa/CobrancaCartao.tsx',
  },
  {
    numero: '017',
    nome: 'Títulos a receber (tabela de baixa)',
    tipo: 'componente',
    oQueFaz: 'A tabela dos títulos que estão sendo baixados: o "Pagar" editável por linha, o total, e a trava do teto quando a baixa está casada com um recebimento (não quita além do que o dinheiro cobre). Pode vir com caixinhas de seleção, pra escolher QUAIS títulos aquele recebimento paga. Só desenha e soma — quem baixa é sempre o mesmo caminho no servidor, que amarra o recebimento a cada título.',
    ondeUsada: 'Contas a Receber: Receber Títulos (parte dos títulos e procura o dinheiro). Conciliação Bancária: aba Contas a Receber do Vincular (parte do Pix que caiu e procura os títulos). São espelhos — mesma baixa, portas opostas.',
    arquivo: 'components/contas-receber/TitulosParaReceber.tsx',
  },
  {
    numero: '018',
    nome: 'Botão de Autorização de retirada',
    tipo: 'componente',
    oQueFaz: 'O botão "Autorização" que imprime o comprovante de retirada (bobina 55mm) que a pessoa que leva a mercadoria assina. Valida "Retirado por" + itens e dispara a impressão. Peça única pra não duplicar a lógica entre a venda e a remessa.',
    ondeUsada: 'Orçamento: modal Enviar ao Caixa (venda no Crediário) e cabeçalho da Remessa em consignação.',
    arquivo: 'components/orcamento/BotaoAutorizacao.tsx',
  },
  {
    numero: '019',
    nome: 'Reconhecer produto pela câmera',
    tipo: 'componente',
    oQueFaz: 'A porta alternativa do campo de busca, pra quem tem a peça na mão e não sabe como a loja a cadastrou: aponta a câmera, tira a foto e o sistema mostra os produtos parecidos do catálogo. Faz o ciclo inteiro numa peça só — o botão, o painel da câmera, a leitura e a lista de candidatos — e devolve o produto escolhido pra quem chamou. A lista agrupa pela PEÇA e deixa as medidas como fichas amarelas, porque a câmera reconhece o tipo e NÃO lê a bitola (por isso ela mostra candidatos e nunca crava um). Sem ninguém acima do corte de confiança, responde "não reconheci" em vez de listar palpites. Some sozinha em aparelho sem câmera ou com o motor fora do ar. Registra cada consulta pra medir, no uso real, em que posição o produto certo aparecia.',
    ondeUsada: 'Orçamento: o campo de busca de produto. Estoque: a busca da tela Estoque por Prateleira (contagem). Arquivos: a busca do cadastro de Produtos. Nas duas últimas ela preenche a busca em vez de adicionar — a lista continua sendo a resposta.',
    arquivo: 'components/reconhecimento/ReconhecerProdutoCamera.tsx',
  },
  {
    numero: '020',
    nome: 'Câmera do aparelho',
    tipo: 'comportamento',
    oQueFaz: 'Todo o trato com a câmera num lugar só: pede a permissão, mostra a imagem ao vivo, congela a foto quando o operador manda, e desliga a lente ao fechar (câmera esquecida ligada trava o aparelho pro próximo uso). Cada falha vira uma frase que diz o que fazer — permissão negada, câmera ocupada por outro aplicativo, aparelho sem câmera —, e não um "erro" genérico. Serve tanto a câmera que vê VOCÊ (rosto) quanto a que vê o que você aponta (produto).',
    ondeUsada: 'Reconhecimento de produto pela câmera (peça 019). Nasceu do cadastro de rosto do ponto, que era o único lugar com esse ciclo tratado direito.',
    arquivo: 'lib/useCameraAparelho.ts',
  },
];
